import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Save, Download, Loader2, Check, BadgeCheck, ScanSearch } from "lucide-react";
import { getQuickEstimate, decodeVin, api } from "../lib/api";
import { fmtMoney, disclaimers } from "../config";
import { MAKES, CATALOG, getModelDef } from "../data/catalog";
import { useAuth } from "../lib/auth";

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 26 }, (_, i) => CURRENT_YEAR + 1 - i);

const CONDITION_OPTS = ["Excellent", "Very Good", "Good", "Fair", "Rough"];
const ACCIDENT_OPTS = ["None", "Minor", "Moderate", "Major"];
const TITLE_OPTS = ["Clean", "Rebuilt", "Salvage", "Lemon", "Flood"];
const TIRE_OPTS = ["New", "Good", "Fair", "Replace"];
const SERVICE_OPTS = ["Complete", "Partial", "None"];
const DRIVETRAIN_OPTS = ["FWD", "RWD", "AWD", "4WD"];

const CAB_TYPES = ["Regular Cab", "Double Cab", "Extended Cab", "Crew Cab"];
const BED_SIZES = ['Short Bed (~5\'8")', 'Standard Bed (~6\'6")', 'Long Bed (~8\')'];
const TRANS_OPTS = ["Automatic", "Manual", "CVT", "DCT / Dual-Clutch", "Single-Speed (EV)"];
const INTERIOR_MATERIALS = ["Cloth", "Leather", "Leatherette / Synthetic", "Suede / Alcantara"];

const BODY_PANELS = [
  { key: "hood", label: "Hood" },
  { key: "roof", label: "Roof" },
  { key: "front_bumper", label: "Front Bumper" },
  { key: "rear_bumper", label: "Rear Bumper" },
  { key: "driver_door", label: "Driver Door" },
  { key: "passenger_door", label: "Passenger Door" },
  { key: "rear_driver_door", label: "Rear Driver Door" },
  { key: "rear_passenger_door", label: "Rear Passenger Door" },
  { key: "driver_fender", label: "Driver Fender" },
  { key: "passenger_fender", label: "Passenger Fender" },
  { key: "driver_quarter", label: "Driver Quarter" },
  { key: "passenger_quarter", label: "Passenger Quarter" },
  { key: "trunk_tailgate", label: "Trunk / Tailgate" },
  { key: "bed", label: "Truck Bed", truckOnly: true },
];
const SEVERITY = [
  { v: "none", label: "None" },
  { v: "minor", label: "Minor (scratch)" },
  { v: "moderate", label: "Moderate (ding/small dent)" },
  { v: "severe", label: "Severe (deep dent/rust)" },
];

export default function QuickTradeEstimator({ testIdPrefix = "qte", onValue }) {
  const [form, setForm] = useState({
    year: "", make: "", model: "", trim: "",
    mileage: "", zip_code: "",
    condition: "Good",
    accidents: "None",
    owners: "1",
    title_status: "Clean",
    tires: "Good",
    service_records: "Partial",
    warning_lights: false,
    drivetrain: "",
    isConvertible: false,
    // VIN + specifics
    vin: "",
    engine: "",
    transmission: "",
    cab_type: "",
    bed_size: "",
    exterior_color: "",
    interior_color: "",
    interior_material: "",
    // Damage
    interior_rips_tears: false,
    interior_stains: false,
    interior_rust: false,
  });
  const [features, setFeatures] = useState([]);
  const [bodyDamage, setBodyDamage] = useState({}); // {panel: 'none|minor|moderate|severe'}
  const [vinDecode, setVinDecode] = useState(null); // {ok, decoded, label, provider, factory_options, original_msrp, window_sticker_url, confidence}
  const [vinBusy, setVinBusy] = useState(false);
  const [vinError, setVinError] = useState("");
  const [confirmedOptions, setConfirmedOptions] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const lastDecodedVin = useRef("");

  const modelDef = useMemo(() => getModelDef(form.make, form.model), [form.make, form.model]);
  const modelOptions = useMemo(() => (form.make && CATALOG[form.make]) || [], [form.make]);
  const isTruck = modelDef?.bodyStyle === "truck";

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((s) => {
      const next = { ...s, [k]: v };
      if (k === "make") { next.model = ""; next.trim = ""; next.isConvertible = false; setFeatures([]); }
      if (k === "model") { next.trim = ""; next.isConvertible = false; setFeatures([]); }
      if (k === "vin") next.vin = (v || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 17);
      return next;
    });
  };

  const toggleFeature = (f) => {
    setFeatures((arr) => arr.includes(f) ? arr.filter((x) => x !== f) : [...arr, f]);
  };
  const toggleConfirmedOption = (o) => {
    setConfirmedOptions((arr) => arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o]);
  };
  const setPanelDamage = (panel, v) => setBodyDamage((s) => ({ ...s, [panel]: v }));

  // Auto-decode VIN when 17 chars
  useEffect(() => {
    const v = form.vin.trim();
    if (v.length !== 17) return;
    if (lastDecodedVin.current === v) return;
    lastDecodedVin.current = v;
    let cancelled = false;
    (async () => {
      setVinBusy(true); setVinError(""); setVinDecode(null);
      try {
        const r = await decodeVin(v);
        if (cancelled) return;
        if (r.ok) {
          setVinDecode(r);
          setConfirmedOptions(r.factory_options || []);
          // Auto-populate where empty (don't clobber user edits)
          setForm((s) => {
            const d = r.decoded || {};
            const matchedMake = MAKES.find((m) => m.toLowerCase() === (d.make || "").toLowerCase()) || s.make;
            const next = { ...s };
            if (!s.year && d.year) next.year = String(d.year);
            if (!s.make && matchedMake) {
              next.make = matchedMake;
              next.model = ""; next.trim = "";
            }
            // Try matching model in catalog with tolerant prefix match
            // (NHTSA returns coarse names like "Silverado"; our catalog has
            // "Silverado 1500" / "Silverado 2500HD" etc.)
            if (matchedMake && (CATALOG[matchedMake] || []).length && d.model && !s.model) {
              const decModel = d.model.toLowerCase();
              const list = CATALOG[matchedMake] || [];
              const matched =
                list.find((mm) => mm.model.toLowerCase() === decModel) ||
                list.find((mm) => mm.model.toLowerCase().startsWith(decModel + " ")) ||
                list.find((mm) => decModel.startsWith(mm.model.toLowerCase())) ||
                list.find((mm) => mm.model.toLowerCase().includes(decModel));
              if (matched) next.model = matched.model;
            }
            if (!s.trim && d.trim) next.trim = d.trim;
            if (!s.drivetrain && d.drivetrain) {
              const dt = d.drivetrain.toLowerCase();
              if (dt.includes("4wd") || dt.includes("4x4") || dt.includes("4-wheel")) next.drivetrain = "4WD";
              else if (dt.includes("awd") || dt.includes("all")) next.drivetrain = "AWD";
              else if (dt.includes("rwd") || dt.includes("rear")) next.drivetrain = "RWD";
              else if (dt.includes("fwd") || dt.includes("front")) next.drivetrain = "FWD";
            }
            if (!s.engine) {
              const parts = [];
              if (d.displacement_l) parts.push(`${d.displacement_l}L`);
              if (d.engine_cyl) parts.push(`${d.engine_cyl}-cyl`);
              if (parts.length) next.engine = parts.join(" ");
            }
            if (!s.transmission && d.transmission_style) {
              const speeds = d.transmission_speeds ? `${d.transmission_speeds}-Speed ` : "";
              next.transmission = `${speeds}${d.transmission_style}`.trim();
            }
            if (!s.cab_type && d.cab_type) next.cab_type = d.cab_type;
            if (!s.bed_size && d.bed_type) next.bed_size = d.bed_type;
            return next;
          });
        } else {
          setVinError(r.error || "VIN couldn't be decoded.");
        }
      } catch (ex) {
        if (!cancelled) setVinError(ex?.response?.data?.detail || "VIN service unavailable.");
      } finally {
        if (!cancelled) setVinBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [form.vin]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setResult(null);
    if (!form.year || !form.make || !form.model || !form.mileage) {
      setError("Please fill in Year, Make, Model, and Mileage.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        year: Number(form.year),
        make: form.make,
        model: form.model || null,
        trim: form.trim || null,
        mileage: Number(form.mileage),
        condition: form.condition,
        body_style: modelDef ? modelDef.bodyStyle : null,
        body_subtype: modelDef ? modelDef.bodySubtype : null,
        accidents: form.accidents,
        owners: Number(form.owners) || 1,
        title_status: form.title_status,
        tires: form.tires,
        service_records: form.service_records,
        warning_lights: !!form.warning_lights,
        zip_code: form.zip_code || null,
        features,
        drivetrain: form.drivetrain || null,
        is_convertible: !!form.isConvertible,
        // New
        vin: form.vin || null,
        engine: form.engine || null,
        transmission: form.transmission || null,
        cab_type: isTruck ? (form.cab_type || null) : null,
        bed_size: isTruck ? (form.bed_size || null) : null,
        exterior_color: form.exterior_color || null,
        interior_color: form.interior_color || null,
        interior_material: form.interior_material || null,
        body_damage: bodyDamage,
        interior_rips_tears: !!form.interior_rips_tears,
        interior_stains: !!form.interior_stains,
        interior_rust: !!form.interior_rust,
        decoded: vinDecode?.decoded || null,
        detected_options: vinDecode?.factory_options || [],
        confirmed_options: confirmedOptions,
        vin_provider: vinDecode?.provider || null,
        vin_msrp: vinDecode?.original_msrp || null,
        vin_window_sticker_url: vinDecode?.window_sticker_url || null,
        vin_confidence: vinDecode?.confidence || null,
      };
      const r = await getQuickEstimate(payload);
      if (r.ok) { setResult(r); onValue && onValue(r); }
      else setError(r.error || "Estimate failed.");
    } catch {
      setError("Something went wrong. Please send your trade details to Scott.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ssc-card" data-testid={`${testIdPrefix}-widget`}>
      <div className="text-[10px] font-extrabold tracking-[0.18em] uppercase text-[var(--ssc-blue)]">Dealer-Grade Trade Valuation</div>
      <h3 className="text-2xl font-black text-[var(--ssc-navy)] mt-1">Instant Trade Value</h3>
      <p className="text-[var(--ssc-ink-2)] text-sm mt-1">
        Wholesale-first multi-factor valuation that thinks like an experienced used-car manager. Enter your VIN to auto-fill specs, or fill in the form by hand.
      </p>

      <form onSubmit={onSubmit} className="mt-4 space-y-5">
        {/* VIN auto-decode */}
        <Fieldset legend="VIN (Optional but Recommended)">
          <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
            <Field label="17-character VIN">
              <div className="relative">
                <input
                  className="ssc-input pr-9 font-mono tracking-wide"
                  value={form.vin}
                  onChange={set("vin")}
                  placeholder="e.g. 1GCUYDED5KZ123456"
                  maxLength={17}
                  autoComplete="off"
                  data-testid={`${testIdPrefix}-vin`}
                />
                {vinBusy && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-[var(--ssc-blue)]" size={16} />
                )}
              </div>
            </Field>
            <div className="text-xs text-[var(--ssc-ink-2)]">
              {form.vin.length}/17
            </div>
          </div>
          {vinError && (
            <div className="mt-2 text-sm text-red-600" data-testid={`${testIdPrefix}-vin-error`}>{vinError}</div>
          )}
          {vinDecode && (
            <DecodedPanel
              decode={vinDecode}
              confirmed={confirmedOptions}
              onToggle={toggleConfirmedOption}
              testIdPrefix={testIdPrefix}
            />
          )}
        </Fieldset>

        {/* Row 1: Vehicle basics */}
        <Fieldset legend="Vehicle">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Year *">
              <select className="ssc-select" value={form.year} onChange={set("year")} data-testid={`${testIdPrefix}-year`}>
                <option value="">Select year</option>
                {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </Field>
            <Field label="Make *">
              <select className="ssc-select" value={form.make} onChange={set("make")} data-testid={`${testIdPrefix}-make`}>
                <option value="">Select make</option>
                {MAKES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </Field>
            <Field label="Model *">
              <select className="ssc-select" value={form.model} onChange={set("model")} disabled={!form.make} data-testid={`${testIdPrefix}-model`}>
                <option value="">{form.make ? "Select model" : "Pick a make first"}</option>
                {modelOptions.map((m) => <option key={m.model} value={m.model}>{m.model}</option>)}
              </select>
            </Field>
            <Field label="Trim">
              <select className="ssc-select" value={form.trim} onChange={set("trim")} disabled={!modelDef} data-testid={`${testIdPrefix}-trim`}>
                <option value="">{modelDef ? "Select trim" : "Pick a model first"}</option>
                {modelDef && modelDef.trims.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Mileage *">
              <input className="ssc-input" inputMode="numeric" value={form.mileage} onChange={set("mileage")} placeholder="e.g. 42000" data-testid={`${testIdPrefix}-mileage`} />
            </Field>
            <Field label="ZIP Code">
              <input className="ssc-input" inputMode="numeric" maxLength={5} value={form.zip_code} onChange={set("zip_code")} placeholder="e.g. 43123" data-testid={`${testIdPrefix}-zip`} />
            </Field>
            <Field label="Drivetrain">
              <select className="ssc-select" value={form.drivetrain} onChange={set("drivetrain")} data-testid={`${testIdPrefix}-drivetrain`}>
                <option value="">Not sure</option>
                {DRIVETRAIN_OPTS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
            {modelDef && modelDef.hasConvertible && (
              <Field label="Body type">
                <label className="flex items-center gap-3 h-[42px] bg-[var(--ssc-paper)] border border-[var(--ssc-line)] rounded-xl px-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.isConvertible}
                    onChange={set("isConvertible")}
                    className="w-5 h-5"
                    data-testid={`${testIdPrefix}-convertible`}
                  />
                  <span className="text-[var(--ssc-ink)] font-semibold text-sm">This is a Convertible</span>
                </label>
              </Field>
            )}
          </div>
        </Fieldset>

        {/* Mechanical / appearance specifics */}
        <Fieldset legend="Specifications & Colors">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Engine">
              <input className="ssc-input" value={form.engine} onChange={set("engine")} placeholder='e.g. 5.3L V8' data-testid={`${testIdPrefix}-engine`} />
            </Field>
            <Field label="Transmission">
              <select className="ssc-select" value={form.transmission} onChange={set("transmission")} data-testid={`${testIdPrefix}-transmission`}>
                <option value="">Select</option>
                {TRANS_OPTS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Exterior Color">
              <input className="ssc-input" value={form.exterior_color} onChange={set("exterior_color")} placeholder="e.g. Summit White" data-testid={`${testIdPrefix}-ext-color`} />
            </Field>
            <Field label="Interior Color">
              <input className="ssc-input" value={form.interior_color} onChange={set("interior_color")} placeholder="e.g. Jet Black" data-testid={`${testIdPrefix}-int-color`} />
            </Field>
            <Field label="Interior Material">
              <select className="ssc-select" value={form.interior_material} onChange={set("interior_material")} data-testid={`${testIdPrefix}-int-material`}>
                <option value="">Select</option>
                {INTERIOR_MATERIALS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
            {isTruck && (
              <>
                <Field label="Cab Type">
                  <select className="ssc-select" value={form.cab_type} onChange={set("cab_type")} data-testid={`${testIdPrefix}-cab`}>
                    <option value="">Select cab</option>
                    {CAB_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
                <Field label="Bed Size">
                  <select className="ssc-select" value={form.bed_size} onChange={set("bed_size")} data-testid={`${testIdPrefix}-bed`}>
                    <option value="">Select bed</option>
                    {BED_SIZES.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
              </>
            )}
          </div>
        </Fieldset>

        {/* Condition + history */}
        <Fieldset legend="Condition & History">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Overall Condition">
              <select className="ssc-select" value={form.condition} onChange={set("condition")} data-testid={`${testIdPrefix}-condition`}>
                {CONDITION_OPTS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Accident History">
              <select className="ssc-select" value={form.accidents} onChange={set("accidents")} data-testid={`${testIdPrefix}-accidents`}>
                {ACCIDENT_OPTS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Previous Owners">
              <select className="ssc-select" value={form.owners} onChange={set("owners")} data-testid={`${testIdPrefix}-owners`}>
                <option value="1">1 (you)</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4+</option>
              </select>
            </Field>
            <Field label="Title Status">
              <select className="ssc-select" value={form.title_status} onChange={set("title_status")} data-testid={`${testIdPrefix}-title`}>
                {TITLE_OPTS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Tire Condition">
              <select className="ssc-select" value={form.tires} onChange={set("tires")} data-testid={`${testIdPrefix}-tires`}>
                {TIRE_OPTS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Service Records">
              <select className="ssc-select" value={form.service_records} onChange={set("service_records")} data-testid={`${testIdPrefix}-service`}>
                {SERVICE_OPTS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Warning Lights" className="sm:col-span-2">
              <label className="flex items-center gap-3 bg-[var(--ssc-paper)] border border-[var(--ssc-line)] rounded-xl px-3 py-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.warning_lights}
                  onChange={set("warning_lights")}
                  className="w-5 h-5"
                  data-testid={`${testIdPrefix}-warning`}
                />
                <span className="text-[var(--ssc-ink)] font-semibold text-sm">Any warning lights on the dash right now</span>
              </label>
            </Field>
          </div>
        </Fieldset>

        {/* Body damage per panel */}
        <Fieldset legend="Body Damage (per panel)">
          <p className="text-xs text-[var(--ssc-ink-2)] mb-3">Mark each panel honestly — Scott uses this to pre-quote your recon work. Default is None.</p>
          <div className="grid sm:grid-cols-2 gap-2" data-testid={`${testIdPrefix}-body-damage`}>
            {BODY_PANELS.filter((p) => !p.truckOnly || isTruck).map((p) => (
              <div key={p.key} className="flex items-center justify-between gap-2 px-3 py-2 bg-[var(--ssc-paper)] border border-[var(--ssc-line)] rounded-xl">
                <span className="text-sm font-semibold text-[var(--ssc-ink)]">{p.label}</span>
                <select
                  className="ssc-select text-xs h-8 py-0 px-2 w-40"
                  value={bodyDamage[p.key] || "none"}
                  onChange={(e) => setPanelDamage(p.key, e.target.value)}
                  data-testid={`${testIdPrefix}-panel-${p.key}`}
                >
                  {SEVERITY.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                </select>
              </div>
            ))}
          </div>
        </Fieldset>

        {/* Interior damage */}
        <Fieldset legend="Interior Condition">
          <div className="grid sm:grid-cols-3 gap-2" data-testid={`${testIdPrefix}-interior-damage`}>
            <ToggleRow label="Rips / tears in seats" testId={`${testIdPrefix}-rips`}
              checked={form.interior_rips_tears} onChange={set("interior_rips_tears")} />
            <ToggleRow label="Stains in carpet / upholstery" testId={`${testIdPrefix}-stains`}
              checked={form.interior_stains} onChange={set("interior_stains")} />
            <ToggleRow label="Rust (floorboards / frame)" testId={`${testIdPrefix}-rust`}
              checked={form.interior_rust} onChange={set("interior_rust")} />
          </div>
        </Fieldset>

        {/* Options/features available for this model */}
        {modelDef && modelDef.features && modelDef.features.length > 0 && (
          <Fieldset legend="Options & Features">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`${testIdPrefix}-features`}>
              {modelDef.features.map((f) => (
                <label key={f} className={`flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer transition-colors ${features.includes(f) ? "border-[var(--ssc-blue)] bg-[var(--ssc-blue)]/5" : "border-[var(--ssc-line)] bg-white"}`}>
                  <input
                    type="checkbox"
                    checked={features.includes(f)}
                    onChange={() => toggleFeature(f)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-[var(--ssc-ink)] font-medium">{f}</span>
                </label>
              ))}
            </div>
          </Fieldset>
        )}

        <button type="submit" disabled={busy} className="ssc-btn ssc-btn-primary w-full sm:w-auto" data-testid={`${testIdPrefix}-submit`}>
          {busy ? "Running dealer valuation…" : "Get My Dealer-Grade Trade Value"}
        </button>
      </form>

      {error && <div className="mt-3 text-sm text-[var(--ssc-red)]" data-testid={`${testIdPrefix}-error`}>{error}</div>}

      {result && <Result r={result} testIdPrefix={testIdPrefix} form={form} features={features}
        modelDef={modelDef} bodyDamage={bodyDamage} confirmedOptions={confirmedOptions} vinDecode={vinDecode} isTruck={isTruck} />}
      {result && (
        <p className="text-xs text-[var(--ssc-ink-2)] mt-3">{disclaimers.trade}</p>
      )}
    </div>
  );
}

const Fieldset = ({ legend, children }) => (
  <fieldset className="border border-[var(--ssc-line)] rounded-2xl p-4">
    <legend className="text-xs font-extrabold tracking-widest uppercase text-[var(--ssc-blue)] px-2">{legend}</legend>
    {children}
  </fieldset>
);

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="ssc-label">{label}</label>
    {children}
  </div>
);

const ToggleRow = ({ label, checked, onChange, testId }) => (
  <label className="flex items-center gap-3 bg-[var(--ssc-paper)] border border-[var(--ssc-line)] rounded-xl px-3 py-3 cursor-pointer">
    <input type="checkbox" checked={checked} onChange={onChange} className="w-5 h-5" data-testid={testId} />
    <span className="text-[var(--ssc-ink)] font-semibold text-sm">{label}</span>
  </label>
);

function DecodedPanel({ decode, confirmed, onToggle, testIdPrefix }) {
  const d = decode.decoded || {};
  const hasFactoryOptions = (decode.factory_options || []).length > 0;
  const items = [
    ["Year", d.year], ["Make", d.make], ["Model", d.model], ["Trim", d.trim],
    ["Body", d.body_class], ["Engine", [d.displacement_l && `${d.displacement_l}L`, d.engine_cyl && `${d.engine_cyl}-cyl`].filter(Boolean).join(" ")],
    ["Transmission", [d.transmission_speeds && `${d.transmission_speeds}-sp`, d.transmission_style].filter(Boolean).join(" ")],
    ["Drivetrain", d.drivetrain], ["Fuel", d.fuel_type],
    ["Cab", d.cab_type], ["Bed", d.bed_type], ["Plant", [d.plant_city, d.plant_country].filter(Boolean).join(", ")],
  ].filter(([, v]) => v);

  return (
    <div className="mt-3 rounded-xl border border-[var(--ssc-blue)]/30 bg-[var(--ssc-blue)]/5 p-4" data-testid={`${testIdPrefix}-vin-decoded`}>
      <div className="flex items-center gap-2 mb-2">
        <BadgeCheck className="text-[var(--ssc-blue)]" size={18} />
        <div className="text-sm font-black text-[var(--ssc-navy)]">{decode.label}</div>
        <span className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-ink-2)] ml-auto">via {decode.provider}</span>
      </div>
      <div className="grid sm:grid-cols-3 gap-x-4 gap-y-1.5">
        {items.map(([k, v]) => (
          <div key={k} className="text-xs">
            <span className="text-[var(--ssc-ink-2)] font-bold uppercase tracking-wider">{k}: </span>
            <span className="text-[var(--ssc-ink)] font-semibold">{v}</span>
          </div>
        ))}
      </div>
      {decode.original_msrp != null && (
        <div className="mt-2 text-xs text-[var(--ssc-ink)]">
          <span className="font-bold">Original MSRP:</span> {fmtMoney(decode.original_msrp)}
          {decode.window_sticker_url && (
            <a href={decode.window_sticker_url} target="_blank" rel="noopener" className="ml-2 text-[var(--ssc-blue)] font-bold underline">
              View window sticker
            </a>
          )}
        </div>
      )}
      {hasFactoryOptions && (
        <div className="mt-3 border-t border-[var(--ssc-blue)]/20 pt-3">
          <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-blue)] mb-2 flex items-center gap-1.5">
            <ScanSearch size={12} /> Detected Factory Options — confirm what's still on the vehicle
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {(decode.factory_options || []).map((o) => (
              <label key={o} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border cursor-pointer text-xs ${confirmed.includes(o) ? "border-[var(--ssc-blue)] bg-white" : "border-[var(--ssc-line)] bg-white/60 opacity-70"}`}>
                <input type="checkbox" checked={confirmed.includes(o)} onChange={() => onToggle(o)} className="w-4 h-4" />
                <span className="text-[var(--ssc-ink)] font-medium">{o}</span>
              </label>
            ))}
          </div>
        </div>
      )}
      {!hasFactoryOptions && decode.note && (
        <div className="mt-2 text-xs text-[var(--ssc-ink-2)] italic">{decode.note}</div>
      )}
    </div>
  );
}

function Result({ r, testIdPrefix, form, features, modelDef, bodyDamage, confirmedOptions, vinDecode, isTruck }) {
  const { user } = useAuth();
  const adminView = r.admin_view;
  const customerView = r.customer_view || {};
  const explainer = r.explainer;
  const [showExplainer, setShowExplainer] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);

  const demandRating = customerView.market || r.demand_rating;
  const confidenceLabel = customerView.confidence_label || "Confidence pending";

  const demandColor = {
    Hot: "bg-red-500/10 text-red-600 border-red-200",
    Strong: "bg-emerald-500/10 text-emerald-700 border-emerald-200",
    Average: "bg-amber-500/10 text-amber-700 border-amber-200",
    Cold: "bg-slate-500/10 text-slate-700 border-slate-200",
  }[demandRating] || "bg-slate-500/10 text-slate-700 border-slate-200";

  return (
    <div className="mt-5 bg-[var(--ssc-paper)] rounded-2xl p-5 border border-[var(--ssc-line)]" data-testid={`${testIdPrefix}-result`}>
      {/* Vehicle summary line (kept high so customer sees what we appraised) */}
      {customerView.vehicle_summary && (
        <div className="text-xs font-extrabold uppercase tracking-widest text-[var(--ssc-blue)] mb-2"
             data-testid={`${testIdPrefix}-vehicle-summary`}>
          {customerView.vehicle_summary}
        </div>
      )}

      {/* Primary CUSTOMER result — single hero range */}
      <div className="rounded-xl bg-white border border-[var(--ssc-line)] p-5 shadow-sm">
        <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-ink-2)]">
          Estimated Dealer Trade Range
        </div>
        <div className="text-3xl md:text-4xl font-black text-[var(--ssc-navy)] mt-1"
             data-testid={`${testIdPrefix}-trade-range`}>
          ${(customerView.trade_low ?? r.dealer_trade_low ?? 0).toLocaleString()}
          <span className="text-[var(--ssc-ink-2)] font-bold"> – </span>
          ${(customerView.trade_high ?? r.dealer_trade_high ?? 0).toLocaleString()}
        </div>
        <div className="mt-3 flex flex-wrap gap-2 items-center">
          <span className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border ${demandColor}`}>
            Market: {demandRating}
          </span>
          <span className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border border-[var(--ssc-line)] bg-white text-[var(--ssc-ink)]"
                data-testid={`${testIdPrefix}-confidence`}>
            {confidenceLabel}
          </span>
          {explainer && (
            <button type="button" onClick={() => setShowExplainer((v) => !v)}
                    className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border border-[var(--ssc-blue)] text-[var(--ssc-blue)] hover:bg-[var(--ssc-blue)]/10 transition"
                    data-testid={`${testIdPrefix}-explainer-toggle`}>
              {showExplainer ? "Hide explanation" : "How we calculate this →"}
            </button>
          )}
        </div>

        {customerView.message && (
          <p className="mt-4 text-sm text-[var(--ssc-ink)] leading-relaxed"
             data-testid={`${testIdPrefix}-customer-message`}>
            {customerView.message}
          </p>
        )}
      </div>

      {/* HOW WE CALCULATE THIS — friendly trust-building explainer */}
      {showExplainer && explainer && (
        <div className="mt-4 bg-white border border-[var(--ssc-line)] rounded-2xl p-5"
             data-testid={`${testIdPrefix}-explainer-panel`}>
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <div className="text-xs font-extrabold uppercase tracking-widest text-[var(--ssc-blue)]">{explainer.title}</div>
              <div className="text-xs text-[var(--ssc-ink-2)] mt-0.5">{explainer.subtitle}</div>
            </div>
            <button type="button" onClick={() => setShowExplainer(false)}
                    className="text-xs text-[var(--ssc-ink-2)] hover:text-[var(--ssc-navy)] px-2 py-1"
                    aria-label="Close explanation">✕</button>
          </div>
          <ol className="space-y-3">
            {(explainer.steps || []).map((s) => (
              <li key={s.step} className="flex gap-3" data-testid={`${testIdPrefix}-explainer-step-${s.step}`}>
                <div className="shrink-0 w-7 h-7 rounded-full bg-[var(--ssc-blue)] text-white text-xs font-black flex items-center justify-center">
                  {s.step}
                </div>
                <div className="text-sm">
                  <div className="font-bold text-[var(--ssc-navy)]">{s.title}</div>
                  <div className="text-[var(--ssc-ink-2)] mt-0.5 leading-relaxed">{s.detail}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Positive / Negative drivers (helpful to customer) */}
      {(r.positive_drivers?.length > 0 || r.negative_drivers?.length > 0) && (
        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          {r.positive_drivers?.length > 0 && (
            <div>
              <div className="text-xs font-extrabold uppercase tracking-widest text-emerald-700">Positive Value Drivers</div>
              <ul className="mt-2 space-y-1 text-sm text-[var(--ssc-ink)]">
                {r.positive_drivers.map((d, i) => <li key={i}>+ {d}</li>)}
              </ul>
            </div>
          )}
          {r.negative_drivers?.length > 0 && (
            <div>
              <div className="text-xs font-extrabold uppercase tracking-widest text-red-700">Negative Value Drivers</div>
              <ul className="mt-2 space-y-1 text-sm text-[var(--ssc-ink)]">
                {r.negative_drivers.map((d, i) => <li key={i}>− {d}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* CTA + save actions */}
      <SaveAndEmailActions r={r} form={form} features={features} modelDef={modelDef}
        bodyDamage={bodyDamage} confirmedOptions={confirmedOptions} vinDecode={vinDecode} isTruck={isTruck}
        testIdPrefix={testIdPrefix} />

      {/* Customer-friendly disclaimer */}
      <p className="text-xs text-[var(--ssc-ink-2)] mt-5" data-testid={`${testIdPrefix}-disclaimer`}>
        {customerView.disclaimer || r.note}
      </p>

      {/* ============================================================
          ADMIN-ONLY: full breakdown — comps, recon items, offer tiers
          Only rendered when the API returns an admin_view payload
          (which it only does for dealer / owner / admin / sales_manager /
          salesperson / bdc / fni / inventory roles).
         ============================================================ */}
      {adminView && (
        <div className="mt-6 border-t-2 border-dashed border-[var(--ssc-blue)]/40 pt-5"
             data-testid={`${testIdPrefix}-admin-view`}>
          <button type="button"
                  onClick={() => setShowAdmin((v) => !v)}
                  className="w-full flex items-center justify-between bg-[var(--ssc-navy)] text-white rounded-xl px-4 py-3 text-sm font-bold hover:opacity-90 transition"
                  data-testid={`${testIdPrefix}-admin-toggle`}>
            <span>Dealer-only Breakdown · {user?.name?.split(" ")[0] || "Internal"} View</span>
            <span className="text-xs opacity-70">{showAdmin ? "Hide ▴" : "Show ▾"}</span>
          </button>
          {showAdmin && (
            <div className="mt-3 bg-white rounded-2xl border border-[var(--ssc-line)] p-5 space-y-4 text-sm">
              <div className="grid sm:grid-cols-2 gap-3">
                <KV label="Segment" value={adminView.segment} />
                <KV label="Starting MSRP" value={`$${(adminView.starting_msrp || 0).toLocaleString()} (${adminView.msrp_source})`} />
                <KV label="Retail Estimate"
                    value={`$${(adminView.retail_estimate.low || 0).toLocaleString()} – $${(adminView.retail_estimate.high || 0).toLocaleString()}`} />
                <KV label="Wholesale (spread)"
                    value={`$${(adminView.wholesale_estimate.value || 0).toLocaleString()} (${adminView.wholesale_estimate.spread_pct})`} />
                <KV label="Trade Range"
                    value={`$${(adminView.trade_range.low || 0).toLocaleString()} – $${(adminView.trade_range.high || 0).toLocaleString()}`} />
                <KV label="Instant Cash" value={`$${(adminView.instant_cash || 0).toLocaleString()}`} />
                <KV label="Confidence" value={`${adminView.confidence}% — ${customerView.confidence_label}`} />
                <KV label="Days to Sell" value={`${adminView.days_to_sell} days`} />
              </div>

              <div>
                <div className="text-xs font-extrabold uppercase tracking-widest text-[var(--ssc-ink-2)] mb-1">
                  Itemized Recon · ${adminView.recon.total.toLocaleString()} total
                </div>
                <ul className="space-y-1">
                  {adminView.recon.items.map((it, i) => (
                    <li key={i} className="flex justify-between border-b border-dashed border-[var(--ssc-line)] py-1">
                      <span>{it.name}</span>
                      <span className="font-bold">${it.cost.toLocaleString()}</span>
                    </li>
                  ))}
                  {adminView.recon.items.length === 0 && <li className="text-[var(--ssc-ink-2)]">No recon items detected.</li>}
                </ul>
              </div>

              <div>
                <div className="text-xs font-extrabold uppercase tracking-widest text-[var(--ssc-ink-2)] mb-1">
                  Internal Offer Tiers
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <TierBox label="Conservative" value={adminView.offer_tiers.conservative} />
                  <TierBox label="Target" value={adminView.offer_tiers.target} primary />
                  <TierBox label="Aggressive" value={adminView.offer_tiers.aggressive} />
                  <TierBox label="Recommended" value={adminView.offer_tiers.recommended} accent />
                </div>
                <div className="text-[11px] text-[var(--ssc-ink-2)] mt-2">
                  Risk buffer: ${adminView.offer_tiers.risk_buffer.toLocaleString()} ·
                  Dealer pack: ${adminView.offer_tiers.dealer_pack.toLocaleString()} ·
                  Desired profit: ${adminView.offer_tiers.desired_profit.toLocaleString()}
                </div>
              </div>

              <div>
                <div className="text-xs font-extrabold uppercase tracking-widest text-[var(--ssc-ink-2)] mb-1">
                  Market Comps ({adminView.comps.count})
                </div>
                <div className="text-xs text-[var(--ssc-ink-2)] mb-2">{adminView.comps.note}</div>
                {adminView.comps.items?.length > 0 && (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-[var(--ssc-ink-2)] uppercase tracking-wider">
                        <th className="py-1">Year</th><th>Vehicle</th><th>Miles</th>
                        <th>Price</th><th>Match</th><th>Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adminView.comps.items.map((c, i) => (
                        <tr key={i} className="border-t border-[var(--ssc-line)]">
                          <td className="py-1.5">{c.year}</td>
                          <td>{[c.make, c.model, c.trim].filter(Boolean).join(" ")}</td>
                          <td>{(c.mileage || 0).toLocaleString()}</td>
                          <td className="font-bold">${(c.price || 0).toLocaleString()}</td>
                          <td>{c.match_score}/100</td>
                          <td>
                            {c.url ? (
                              <a href={c.url} target="_blank" rel="noopener noreferrer"
                                 className="text-[var(--ssc-blue)] underline">{c.source}</a>
                            ) : (c.source || "—")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div className="bg-[var(--ssc-paper)] rounded-lg p-2.5">
      <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--ssc-ink-2)]">{label}</div>
      <div className="text-sm font-bold text-[var(--ssc-navy)] mt-0.5">{value || "—"}</div>
    </div>
  );
}

function TierBox({ label, value, primary, accent }) {
  const cls = primary
    ? "bg-[var(--ssc-blue)]/10 border-[var(--ssc-blue)] text-[var(--ssc-blue)]"
    : accent
    ? "bg-emerald-50 border-emerald-300 text-emerald-800"
    : "bg-white border-[var(--ssc-line)] text-[var(--ssc-navy)]";
  return (
    <div className={`rounded-lg border-2 p-2.5 text-center ${cls}`}>
      <div className="text-[10px] font-bold uppercase tracking-widest opacity-80">{label}</div>
      <div className="text-lg font-black mt-0.5">${(value || 0).toLocaleString()}</div>
    </div>
  );
}

function SaveAndEmailActions({ r, form, features, modelDef, bodyDamage, confirmedOptions, vinDecode, isTruck, testIdPrefix }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(null);
  const [savedCode, setSavedCode] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [emailForm, setEmailForm] = useState({
    name: user?.name || "", email: user?.email || "", phone: user?.phone || "",
  });
  const [showEmailForm, setShowEmailForm] = useState(false);

  const vehiclePayload = () => ({
    year: Number(form.year), make: form.make, model: form.model, trim: form.trim,
    mileage: Number(form.mileage), zip_code: form.zip_code,
    condition: form.condition, body_style: modelDef?.bodyStyle,
    body_subtype: modelDef?.bodySubtype, features,
    accidents: form.accidents, owners: Number(form.owners),
    title_status: form.title_status, tires: form.tires,
    service_records: form.service_records,
    drivetrain: form.drivetrain,
    is_convertible: !!form.isConvertible,
    // New rich fields, persisted with the lead so Scott sees them in admin
    vin: form.vin || null,
    engine: form.engine || null,
    transmission: form.transmission || null,
    cab_type: isTruck ? form.cab_type || null : null,
    bed_size: isTruck ? form.bed_size || null : null,
    exterior_color: form.exterior_color || null,
    interior_color: form.interior_color || null,
    interior_material: form.interior_material || null,
    body_damage: bodyDamage,
    interior_rips_tears: !!form.interior_rips_tears,
    interior_stains: !!form.interior_stains,
    interior_rust: !!form.interior_rust,
    decoded: vinDecode?.decoded || null,
    detected_options: vinDecode?.factory_options || [],
    confirmed_options: confirmedOptions,
    vin_provider: vinDecode?.provider || null,
    vin_msrp: vinDecode?.original_msrp || null,
    vin_window_sticker_url: vinDecode?.window_sticker_url || null,
    vin_confidence: vinDecode?.confidence || null,
  });

  const saveDeal = async () => {
    setBusy("save"); setErr(""); setMsg("");
    try {
      const { data } = await api.post("/deals", {
        deal_type: "trade", vehicle: vehiclePayload(), estimate: r,
      });
      setSavedCode(data.deal.code);
      setMsg(`Saved as ${data.deal.code}`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't save.");
    } finally { setBusy(null); }
  };

  const downloadPdf = async () => {
    setBusy("pdf"); setErr("");
    try {
      const resp = await api.post(
        "/trade/pdf",
        { name: emailForm.name || user?.name || "Customer", email: emailForm.email || user?.email || "noreply@example.com", phone: emailForm.phone || user?.phone, vehicle: vehiclePayload(), estimate: r, consent: true },
        { responseType: "blob" },
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "ScottSellsChevys-Trade-Estimate.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setErr("Couldn't generate PDF.");
    } finally { setBusy(null); }
  };

  const emailEstimate = async (e) => {
    e?.preventDefault?.();
    if (!emailForm.email || !emailForm.name) { setErr("Name and email required."); return; }
    setBusy("email"); setErr(""); setMsg("");
    try {
      const { data } = await api.post("/trade/email-estimate", {
        ...emailForm, vehicle: vehiclePayload(), estimate: r, consent: true,
      });
      if (data.deal_code) setSavedCode(data.deal_code);
      setMsg(`Sent! Check ${emailForm.email}. ${data.deal_code ? "Your code: " + data.deal_code : ""}`);
      setShowEmailForm(false);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Couldn't send. Try again.");
    } finally { setBusy(null); }
  };

  return (
    <div className="mt-5 pt-5 border-t border-[var(--ssc-line)]" data-testid={`${testIdPrefix}-actions`}>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setShowEmailForm((v) => !v)} disabled={busy != null}
                className="ssc-btn ssc-btn-primary text-sm inline-flex items-center gap-2"
                data-testid={`${testIdPrefix}-email-toggle`}>
          <Mail size={15} /> {showEmailForm ? "Hide email form" : "Save & Email Me a PDF"}
        </button>
        <button onClick={downloadPdf} disabled={busy != null}
                className="ssc-btn ssc-btn-secondary text-sm inline-flex items-center gap-2"
                data-testid={`${testIdPrefix}-download`}>
          {busy === "pdf" ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download PDF
        </button>
        {user ? (
          <button onClick={saveDeal} disabled={busy != null}
                  className="ssc-btn ssc-btn-secondary text-sm inline-flex items-center gap-2"
                  data-testid={`${testIdPrefix}-save`}>
            {busy === "save" ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save to My Account
          </button>
        ) : (
          <Link to="/signup" className="ssc-btn ssc-btn-secondary text-sm inline-flex items-center gap-2"
                data-testid={`${testIdPrefix}-signup-cta`}>
            <Save size={15} /> Sign Up to Save
          </Link>
        )}
      </div>

      {showEmailForm && (
        <form onSubmit={emailEstimate} className="mt-4 grid sm:grid-cols-3 gap-2 bg-white p-4 rounded-xl border border-[var(--ssc-line)]" data-testid={`${testIdPrefix}-email-form`}>
          <input className="ssc-input" placeholder="Your name *" value={emailForm.name} onChange={(e) => setEmailForm({ ...emailForm, name: e.target.value })} required data-testid={`${testIdPrefix}-email-name`} />
          <input className="ssc-input" placeholder="Email *" type="email" value={emailForm.email} onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })} required data-testid={`${testIdPrefix}-email-email`} />
          <input className="ssc-input" placeholder="Phone (optional)" value={emailForm.phone} onChange={(e) => setEmailForm({ ...emailForm, phone: e.target.value })} data-testid={`${testIdPrefix}-email-phone`} />
          <button type="submit" disabled={busy != null} className="ssc-btn ssc-btn-primary sm:col-span-3 text-sm inline-flex items-center justify-center gap-2" data-testid={`${testIdPrefix}-email-submit`}>
            {busy === "email" ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />}
            Email My Estimate
          </button>
        </form>
      )}

      {msg && (
        <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-900 inline-flex items-center gap-2" data-testid={`${testIdPrefix}-actions-msg`}>
          <Check size={15} /> {msg}
        </div>
      )}
      {savedCode && (
        <div className="mt-2 text-xs text-[var(--ssc-ink-2)]">
          Your deal code is <span className="font-mono font-bold text-[var(--ssc-navy)]">{savedCode}</span> — share this with Scott to pull up your estimate.
        </div>
      )}
      {err && <div className="mt-3 text-sm text-red-600" data-testid={`${testIdPrefix}-actions-err`}>{err}</div>}
    </div>
  );
}
